import type { FC, ReactNode } from "react"
import { useEffect, useRef } from "react"
import { Box, Group, Text, Button, Stack, UnstyledButton } from "@mantine/core"
import {
	AEM_DICTIONARY_COLLECTIONS,
	useAEMDictionary
} from "@AEM/hooks/useAEMDictionary"
import {
	showSuccessNotification,
	showWarningNotification
} from "../Notification/Notification"
import { useReviewItemEditStore } from "@stores/reviewItemEditStore"

interface ReviewItemWrapperProps {
	children: ReactNode
	label: string
	value: string
	valuePreview?: ReactNode
	isReviewMode?: boolean
	isRequired?: boolean
	onCancel?: (value: string) => void
	onSave?: (value: string) => boolean
	onEditingChange?: (isEditing: boolean) => void
	validationError?: string
}

export const ReviewItemWrapper: FC<ReviewItemWrapperProps> = ({
	children,
	label,
	value,
	valuePreview,
	isReviewMode = false,
	isRequired = false,
	onCancel,
	onSave,
	onEditingChange,
	validationError
}) => {
	const { dictionary } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.REGISTRATION
	)

	const { currentEditId, itemStates, startEdit, cancelEdit, saveEdit } =
		useReviewItemEditStore()

	const editIdRef = useRef<string>()
	if (!editIdRef.current) {
		editIdRef.current = `${label}-${Math.random().toString(36).substr(2, 9)}`
	}
	const editId = editIdRef.current

	const itemState = itemStates[editId] || {
		isEditing: false,
		lastSavedValue: value,
		showWarning: false,
		isButtonDisabled: false
	}

	useEffect(() => {
		if (!itemState?.isEditing) {
			onCancel?.(itemState.lastSavedValue)
		}
		onEditingChange?.(!!itemState?.isEditing)
	}, [itemState?.isEditing])

	// An unmount while editing would otherwise leave a stale edit lock in the store
	useEffect(
		() => () => {
			const store = useReviewItemEditStore.getState()
			if (store.itemStates[editId]?.isEditing) store.cancelEdit(editId)
		},
		[editId]
	)

	const handleEdit = () => {
		const hasEditConflict =
			!!currentEditId &&
			currentEditId !== editId &&
			itemStates[currentEditId]?.isEditing

		if (hasEditConflict) {
			showWarningNotification(
				"",
				dictionary?.editConflictWarningLabel ??
					"Save or cancel the field currently being edited before opening another one.",
				{ id: "review-item-edit-conflict" }
			)
		}

		startEdit(editId, value)
	}

	const handleCancel = () => {
		cancelEdit(editId)
		onCancel?.(itemState.lastSavedValue)
	}

	const handleSave = () => {
		if (isRequired && (!value || value.trim() === "")) {
			return
		}

		if (onSave && !onSave(value)) {
			return
		}

		saveEdit(editId, value)
		showSuccessNotification(`${label} ${dictionary?.fieldNameUpdatedLabel}`)
	}

	const isSaveDisabled = isRequired && (!value || value.trim() === "")

	if (!isReviewMode || itemState.isEditing) {
		return (
			<Group gap={10} wrap="nowrap" align="flex-start">
				<Box style={{ flex: 1 }}>{children}</Box>
				{isReviewMode && (
					<Stack gap={0} justify="space-between" mih="100%">
						<UnstyledButton
							onClick={handleCancel}
							fz={14}
							ta={"right"}
							lh={1.6}
							td="underline"
							fw={600}
							mb={3}
							mr={3}
						>
							{dictionary?.cancelButtonLabel}
						</UnstyledButton>

						<Button
							variant="dark-blue"
							maw={79}
							h={38}
							fz={12}
							fw={600}
							onClick={handleSave}
							disabled={isSaveDisabled}
							style={{
								cursor: isSaveDisabled ? "not-allowed" : "pointer"
							}}
						>
							{dictionary?.contentSaveLink}
						</Button>
					</Stack>
				)}
			</Group>
		)
	}

	return (
		<Stack gap={10} justify="flex-start">
			<Group gap={10} justify="space-between" wrap="nowrap" align="flex-start">
				<Group gap={5} wrap="nowrap">
					<Text fz={18} fw={500} lh={1.5}>
						{label}
					</Text>
					{isRequired && (
						<Text fz={18} fw={500} lh={1.5}>
							*
						</Text>
					)}
				</Group>
				<UnstyledButton
					onClick={handleEdit}
					fz={14}
					lh={1.6}
					td="underline"
					fw={600}
					opacity={itemState.isButtonDisabled ? 0.5 : 1}
					aria-label={`${dictionary?.contentEditLink} ${label}`}
					aria-disabled={itemState.isButtonDisabled}
					style={{
						cursor: itemState.isButtonDisabled ? "not-allowed" : "pointer"
					}}
					disabled={itemState.isButtonDisabled}
				>
					{dictionary?.contentEditLink}
				</UnstyledButton>
			</Group>
			<Text fz={18} fw={600} lh={1.5}>
				{valuePreview ? valuePreview : value || "-"}
			</Text>
			{validationError && (
				<Text fz={12} c="red">
					{validationError}
				</Text>
			)}
		</Stack>
	)
}
